import { useEffect, useRef, useState } from "react";
import useDebouncedValue from "./useDebouncedValue";

/**
 * Live server-side sheet search.
 *
 * Owns the text the user is typing and pushes it into `pagination.searchTerm`
 * once they stop, so the sheet filters itself without a "Go" button. The
 * useSheetData query key includes searchTerm, so writing it is what triggers
 * the refetch.
 *
 * 350ms (vs the header bar's 250ms) because every push here costs a paginated
 * round-trip, not a cached lookup.
 *
 * Returns `submitNow` for Enter, which bypasses the debounce.
 */
export default function useServerSearch(pagination, setPagination, { delay = 350 } = {}) {
    //When there's no pagination the caller is filtering client-side over the
    //loaded rows (the mobile sheet does this on unpaginated sheets), so this
    //degrades to plain local state and never commits anything.
    const enabled = !!(pagination && setPagination);
    const committed = pagination?.searchTerm ?? "";
    const [searchTerm, setSearchTerm] = useState(committed);
    const debounced = useDebouncedValue(searchTerm, delay);

    //The committed value can change underneath us (Clear button, tab switch).
    //Track it so an external reset re-syncs the input instead of the stale
    //local state immediately re-pushing the old term back.
    const lastCommitted = useRef(committed);
    useEffect(() => {
        if (committed !== lastCommitted.current) {
            lastCommitted.current = committed;
            setSearchTerm(committed);
        }
    }, [committed]);

    const commit = (term) => {
        if (!enabled) return;
        const next = term.trim();
        //No-op when nothing actually changed — otherwise mounting the sheet, or
        //any unrelated re-render, would reset the user back to page 1.
        if (next === (lastCommitted.current ?? "")) return;
        lastCommitted.current = next;
        setPagination((prev) => ({ ...prev, searchTerm: next, currentPage: 1 }));
    };

    useEffect(() => {
        commit(debounced);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced]);

    return {
        searchTerm,
        setSearchTerm,
        //Enter shouldn't make the user wait out the debounce.
        submitNow: () => commit(searchTerm),
        clearSearch: () => {
            setSearchTerm("");
            commit("");
        },
    };
}
